import { AutomationNodeType, AutomationActionNodeSubType } from '@frontend/app/types/globalTypes';
import { AutomationNodeV2 } from '../types';

// Default edge labels for different contexts
export const DEFAULT_EDGE_LABELS = {
  // Condition node branches
  condition: {
    true: 'True',
    false: 'False',
  },

  // Action nodes by subtype - these are NOT used as defaults, just for action type identification
  action: {
    [AutomationActionNodeSubType.SEND_EMAIL]: 'Send Email',
    [AutomationActionNodeSubType.HANDLE_PFA_BOOST]: 'Apply Boost',
    [AutomationActionNodeSubType.HANDLE_DISCOUNT_CODE_BOOST]: 'Apply Discount',
    [AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST]: 'Apply Cashback',
    [AutomationActionNodeSubType.UPDATE_MEMBER_DATA]: 'Update Data',
    [AutomationActionNodeSubType.ADD_TO_OFFER]: 'Add to Offer',
    [AutomationActionNodeSubType.SEND_OFFER_DETAILS]: 'Send Details',
    [AutomationActionNodeSubType.SEND_PRODUCT_CATALOG]: 'Send Catalog',
    default: 'Execute',
  },

  // Edge leading TO a delay node (indicating transition to wait state)
  delay: 'Wait',

  // Branch nodes
  branch: (index: number) => `Path ${index + 1}`,

  // Generic default
  default: 'Then',
};

// Maximum allowed length for edge labels
export const MAX_EDGE_LABEL_LENGTH = 20;

/**
 * Get the default edge label for a node based on its type and context
 */
export function getDefaultEdgeLabel(
  node: AutomationNodeV2,
  parentNode?: AutomationNodeV2,
  isElseBranch?: boolean,
  branchIndex?: number,
): string {
  // For condition node children
  if (parentNode?.type === AutomationNodeType.CONDITION) {
    return isElseBranch ? DEFAULT_EDGE_LABELS.condition.false : DEFAULT_EDGE_LABELS.condition.true;
  }

  // For branch nodes (custom type not in AutomationNodeType enum)
  if ((parentNode as any)?.type === 'BRANCH' && typeof branchIndex === 'number') {
    return DEFAULT_EDGE_LABELS.branch(branchIndex);
  }

  // For edges leading TO different node types - what makes sense for the transition
  switch (node.type) {
    case AutomationNodeType.DELAY:
      // Edge leading TO a delay node should indicate transition to waiting
      return DEFAULT_EDGE_LABELS.delay;

    case AutomationNodeType.ACTION:
      // Edge leading TO an action node - use generic default, not subtype-specific
      return DEFAULT_EDGE_LABELS.default;

    case AutomationNodeType.CONDITION:
      // Edge leading TO a condition node
      return DEFAULT_EDGE_LABELS.default;

    default:
      return DEFAULT_EDGE_LABELS.default;
  }
}

/**
 * Validate an edge label
 */
export function validateEdgeLabel(label: string): { valid: boolean; error?: string } {
  const trimmed = (label ?? '').trim();
  if (!trimmed || trimmed.length === 0) {
    return { valid: false, error: 'Label cannot be empty' };
  }

  if (trimmed.length > MAX_EDGE_LABEL_LENGTH) {
    return { valid: false, error: `Label must be ${MAX_EDGE_LABEL_LENGTH} characters or less` };
  }

  // Check for invalid characters (optional - adjust based on requirements)
  const invalidChars = /[<>]/;
  if (invalidChars.test(trimmed)) {
    return { valid: false, error: 'Label contains invalid characters' };
  }

  return { valid: true };
}

/**
 * Format label for display (trim and capitalize appropriately)
 */
export function formatEdgeLabel(label: string): string {
  return label.trim().substring(0, MAX_EDGE_LABEL_LENGTH);
}
